// Site-level structured data builders. Plain CommonJS, shared with scripts/prerender.js.
const site = require("./site");
const tourSeo = require("./tourSeo");

const ORG_ID = site.BASE_URL + "/#organization";

function postalAddress() {
  return {
    "@type": "PostalAddress",
    streetAddress: site.NAP.streetAddress,
    addressLocality: site.NAP.addressLocality,
    addressRegion: site.NAP.addressRegion,
    postalCode: site.NAP.postalCode,
    addressCountry: site.NAP.addressCountry,
  };
}

/** Rendered on every page. Shares its @id with the LocalBusiness so they are one entity. */
function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: site.SITE_NAME,
    url: site.BASE_URL + "/",
    logo: { "@type": "ImageObject", url: site.LOGO },
    sameAs: site.SAME_AS,
    telephone: site.NAP.telephone,
    email: site.NAP.email,
    foundingDate: site.FOUNDING_DATE,
    address: postalAddress(),
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: site.NAP.telephone,
        email: site.NAP.email,
        contactType: "customer service",
        areaServed: "US",
        availableLanguage: "English",
      },
    ],
  };
}

/** Home page only. No self-reported aggregateRating: Google ignores it and the old blocks disagreed. */
function buildLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": ORG_ID,
    name: site.SITE_NAME,
    description: site.SUMMARY,
    url: site.BASE_URL + "/",
    telephone: site.NAP.telephone,
    email: site.NAP.email,
    image: [site.DEFAULT_OG_IMAGE],
    logo: site.LOGO,
    address: postalAddress(),
    geo: { "@type": "GeoCoordinates", latitude: site.GEO.latitude, longitude: site.GEO.longitude },
    hasMap: site.MAPS_URL,
    priceRange: site.PRICE_RANGE,
    openingHoursSpecification: site.OPENING_HOURS,
    areaServed: site.SERVICE_AREA.map(function (name) {
      return { "@type": "City", name: name };
    }),
    sameAs: site.SAME_AS,
    foundingDate: site.FOUNDING_DATE,
  };
}

/** The list of enabled tours, replacing one Product block per card on the home page. */
function buildItemListSchema(tours) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": site.BASE_URL + "/#tours",
    name: "American Legacy Tours walking tours",
    numberOfItems: tours.length,
    itemListElement: tours.map(function (tour, i) {
      return { "@type": "ListItem", position: i + 1, name: tour.title, url: tourSeo.tourUrl(tour) };
    }),
  };
}

function buildFaqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(function (faq) {
      return {
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      };
    }),
  };
}

/** Tour-guide posting. validThrough rolls forward so the posting never silently expires. */
function buildJobPostingSchema(now) {
  const current = now || new Date();
  const validThrough = new Date(current.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: "Tour Guide",
    description:
      "<p>Looking for a fun job in Cincinnati or Newport? Now hiring tour guides! We're looking for enthusiastic and entertaining people who can project their voice in front of a large crowd. Must be available on weekends and weekdays if possible.</p>",
    identifier: { "@type": "PropertyValue", name: site.SITE_NAME, value: "tour-guide" },
    datePosted: "2022-09-21",
    validThrough: validThrough,
    employmentType: "CONTRACTOR",
    hiringOrganization: { "@type": "Organization", "@id": ORG_ID, name: site.SITE_NAME, sameAs: site.BASE_URL + "/", logo: site.LOGO },
    jobLocation: { "@type": "Place", address: postalAddress() },
    baseSalary: {
      "@type": "MonetaryAmount",
      currency: "USD",
      value: { "@type": "QuantitativeValue", value: 12.0, unitText: "HOUR" },
    },
  };
}

module.exports = {
  ORG_ID: ORG_ID,
  buildOrganizationSchema: buildOrganizationSchema,
  buildLocalBusinessSchema: buildLocalBusinessSchema,
  buildItemListSchema: buildItemListSchema,
  buildFaqSchema: buildFaqSchema,
  buildJobPostingSchema: buildJobPostingSchema,
};
